/*
 * <yard-bundle-builder>
 *
 * Lets the customer pick exactly which yard games they want, keeps every game
 * at its own price, and applies a tiered discount to the combined total:
 *
 *   2 games -> $5 off, 3 -> $10, 4 -> $15, 5 or more -> $20
 *
 * The selected games are added to the rental list as individual line items, so
 * the request that reaches the merchant lists each game by name with its own
 * price. The theme recalculates the same discount on the cart and on the Event
 * Details page from the cart contents, so the figure the customer sees here is
 * the figure that travels with the request.
 */

if (!customElements.get('yard-bundle-builder')) {
  customElements.define(
    'yard-bundle-builder',
    class YardBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-yard-form]');
        this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-yard-game]'));
        this.submitButton = this.querySelector('[data-yard-submit]');
        this.errorElement = this.querySelector('[data-yard-error]');
        this.nextTierElement = this.querySelector('[data-yard-next]');
        this.tierElements = Array.prototype.slice.call(this.querySelectorAll('[data-yard-tier]'));
        this.submitting = false;

        this.output = {
          count: this.querySelector('[data-yard-count]'),
          selected: this.querySelector('[data-yard-selected]'),
          subtotal: this.querySelector('[data-yard-subtotal]'),
          discountRow: this.querySelector('[data-yard-discount-row]'),
          discount: this.querySelector('[data-yard-discount]'),
          total: this.querySelector('[data-yard-total]'),
        };

        var self = this;
        this.inputs.forEach(function (input) {
          input.addEventListener('change', function () {
            self.render();
          });
        });

        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

        this.render();
      }

      /* Discount tiers, in cents, keyed by how many distinct games are chosen. */
      discountFor(count) {
        if (count >= 5) return 2000;
        if (count === 4) return 1500;
        if (count === 3) return 1000;
        if (count === 2) return 500;
        return 0;
      }

      nextTierMessage(count) {
        if (count === 0) return 'Pick 2 games to save $5.';
        if (count === 1) return 'Add 1 more game to save $5.';
        if (count === 2) return 'Add 1 more game to save $10 instead of $5.';
        if (count === 3) return 'Add 1 more game to save $15 instead of $10.';
        if (count === 4) return 'Add 1 more game to save $20 instead of $15.';
        return '';
      }

      get chosen() {
        return this.inputs.filter(function (input) {
          return input.checked;
        });
      }

      formatMoney(cents) {
        var amount = (Math.round(cents) / 100).toFixed(2);
        var symbol = this.dataset.moneySymbol || '$';
        return symbol + amount;
      }

      render() {
        var chosen = this.chosen;
        var count = chosen.length;

        var subtotal = chosen.reduce(function (sum, input) {
          return sum + parseInt(input.dataset.price, 10);
        }, 0);

        var discount = this.discountFor(count);
        var total = Math.max(subtotal - discount, 0);

        if (this.output.count) {
          this.output.count.textContent = count === 1 ? '1 game selected' : count + ' games selected';
        }

        if (this.output.selected) {
          this.output.selected.textContent = count
            ? chosen
                .map(function (input) {
                  return input.dataset.title;
                })
                .join(', ')
            : 'Nothing selected yet.';
        }

        if (this.output.subtotal) this.output.subtotal.textContent = this.formatMoney(subtotal);
        if (this.output.discount) this.output.discount.textContent = '-' + this.formatMoney(discount);
        if (this.output.total) this.output.total.textContent = this.formatMoney(total);
        if (this.output.discountRow) this.output.discountRow.hidden = discount === 0;

        this.tierElements.forEach(function (tier) {
          var tierCount = parseInt(tier.dataset.yardTier, 10);
          var isActive = tierCount === 5 ? count >= 5 : count === tierCount;
          tier.classList.toggle('yard-bundle__tier--active', isActive);
        });

        if (this.nextTierElement) {
          var message = this.nextTierMessage(count);
          this.nextTierElement.textContent = message;
          this.nextTierElement.hidden = message === '';
        }

        if (this.submitButton) {
          var blocked = count === 0 || this.submitting;
          this.submitButton.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        }
      }

      showError(message) {
        if (!this.errorElement) return;
        this.errorElement.textContent = message;
        this.errorElement.hidden = !message;
      }

      onSubmit(event) {
        event.preventDefault();

        if (this.submitting) return;

        var chosen = this.chosen;
        if (!chosen.length) {
          this.showError('Choose at least one game to add to your rental list.');
          return;
        }

        this.showError('');
        this.submitting = true;
        this.render();

        var label = this.submitButton ? this.submitButton.querySelector('[data-yard-submit-label]') : null;
        var originalLabel = label ? label.textContent : '';
        if (label) label.textContent = 'Adding to your rental list...';

        var items = chosen.map(function (input) {
          return { id: parseInt(input.value, 10), quantity: 1 };
        });

        var root = (window.rentalRoutes && window.rentalRoutes.rootUrl) || '/';
        var cartUrl = (window.rentalRoutes && window.rentalRoutes.cartUrl) || '/cart';
        var self = this;

        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw new Error(data.description || data.message || 'Could not add these games.');
              return data;
            });
          })
          .then(function () {
            window.location.href = cartUrl;
          })
          .catch(function (error) {
            self.submitting = false;
            if (label) label.textContent = originalLabel;
            self.showError(error.message || 'Something went wrong. Please try again.');
            self.render();
          });
      }
    }
  );
}
